import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../../../common/hooks/use-edit-page-prime-state';
import { useShowInactiveBannerFlag } from '../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { InactiveChannelAlertSmart } from '../../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialogueSelectorWithReload } from '../../../startBotPromoters/common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { InstallScriptInstructions } from '../../../startBotPromoters/common/InstallScriptInstructions/InstallScriptInstuctions';
import { EditStartBotHeader } from '../../../startBotPromoters/common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { StartBotsDialogueEditor } from '../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { usePromoterJSCode } from '../../../startBotPromoters/common/use-promoter-js-code';
import DomainsField from '../../components/DomainsField/DomainsField';
import { PromotersType } from '../../PromotersPanel/use-prime-state';
import { LeadPopupConfirmationForm } from './LeadPopupConfirmationForm/LeadPopupConfirmationForm';
import { LeadPopupDesignForm } from './LeadPopupDesignForm/LeadPopupDesignForm';
import './LeadsPromoterPanelEdit.pcss';
import { SmsIphonePreview } from '../../../campaigns/components/SmsIphonePreview/SmsIphonePreview';

const kind: PromotersType = 'optin';

export const LeadsPromoterPanelEdit = () => {
  const { widgets, isFetching, page, promoter } = useEditPagePrimeState(kind);
  const chatbot = useSelector(activeChatbotSelector);
  const promoterJSCode = usePromoterJSCode(promoter, page);
  const { update } = usePromoterCRUDActions();

  const domain = useSelector(domainSelector);
  const domainCRUDActions = useDomainCRUDActions();

  const { openActivateInactiveBotModal } = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const promoterCRUDActions = usePromoterCRUDActions();
  const updatePromoter = () => {
    if (promoter) {
      promoterCRUDActions.update(chatbot.id, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };
  const customActivateAction = () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else {
        updatePromoter();
      }
    }
  };
  const onDomainUpdate = (domainsList: string[]) => {
    const { id, connectedPageId } = domain;
    if (!connectedPageId) return; // domains are fetched in page prime state hook
    domainCRUDActions.update({ id, connectedPageId, domainsList });
  };

  const pathGraph = useNewGraphInstance(promoter?.pageId);

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.leadPopupsEdit.getLink({
            chatbotId: chatbot.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
      />
    ) : null;
  }, [promoter, chatbot.id]);

  if (!promoter || !page || isFetching) return null;

  const onPromoterUpdate = (promoter: PromoterModel) =>
    update(chatbot.id, { ...promoter, enabledOptins: ['sms'] });

  return (
    <div className="leads-promoter-panel-edit">
      <InactiveChannelAlertSmart sms type="Lead Popup" />

      <EditStartBotHeader
        promoter={promoter}
        page={page}
        botIsTemplate={!!chatbot.template}
        customActivateAction={customActivateAction}
      />

      <CollapsibleContentContainer orderNumber="1" containerName="Popup design">
        <LeadPopupDesignForm promoter={promoter} onUpdate={onPromoterUpdate} />
      </CollapsibleContentContainer>

      <CollapsibleContentContainer
        orderNumber="2"
        containerName="Popup confirmation"
      >
        <LeadPopupConfirmationForm
          promoter={promoter}
          onUpdate={onPromoterUpdate}
        />
      </CollapsibleContentContainer>

      <CollapsibleContentContainer
        orderNumber="3"
        containerName="Choose or Create a Dialogue"
      >
        <div className="leads-promoter-panel-edit__dialogue-container">
          {promoter && (
            <PathGraphContext.Provider value={pathGraph}>
              <div className="leads-promoter-panel-edit__dialogue">
                {dialogueSelectorDropdown}
                <StartBotsDialogueEditor
                  chatbotId={chatbot.id}
                  widgets={widgets}
                  dialogueId={promoter.pageId}
                  isSmsRelated={true}
                  smsOnly={true}
                  platforms={['sms']}
                  widgetsToExclude={[
                    WidgetTypes.GALLERY,
                    WidgetTypes.ATTACHMENT,
                    WidgetTypes.TYPING,
                    WidgetTypes.OTN,
                  ]}
                />
              </div>
              <SmsIphonePreview
                className="leads-promoter-panel-edit__sms-preview"
                destinationPageId={promoter.pageId}
                testNumber={false}
              />
            </PathGraphContext.Provider>
          )}
        </div>
      </CollapsibleContentContainer>

      <CollapsibleContentContainer
        orderNumber="4"
        containerName="Install Opt-in Popup Plugin"
      >
        <DomainsField
          domains={domain.domainsList}
          updateDomains={onDomainUpdate}
        />

        <InstallScriptInstructions
          codeToCopy={promoterJSCode}
          helpLinkHref="https://customers.ai/help/article/0fah7phh3c-sms-popups"
        />
      </CollapsibleContentContainer>
    </div>
  );
};
